//
// Bridge between the Logto SDK and Convex's `useAuth` contract.
//
// Convex's `ConvexProviderWithAuth` expects a `useAuth()` hook that returns:
//   { isLoading, isAuthenticated, fetchAccessToken }
//
// `fetchAccessToken` is called by Convex on every request. It MUST NOT throw —
// throwing causes an unhandled promise rejection. Returning `null` tells Convex
// the user is unauthenticated (after one retry with `forceRefreshToken: true`).
//
// ── Two auth modes ────────────────────────────────────────────────────────
//
// STANDALONE (direct navigation to https://{appId}.app.sapps.co):
//   Uses @logto/browser SDK. Checks localStorage for a refresh token; if absent,
//   redirects to Logto for silent SSO. `getAccessToken(resource)` mints an
//   RS256 JWT scoped to SIMPLEAPP_LOGTO_AUDIENCE.
//
// IFRAMED (embedded inside the Sapps shell at https://sapps.co):
//   MUST NOT use the Logto refresh-token grant — third-party cookie blocking in
//   Safari/Chrome breaks it. Instead, requests a token from the parent shell
//   via postMessage (`use-parent-token-auth.ts`). The parent calls
//   sapps-backend which performs Logto RFC 8693 token exchange. The relayed
//   token is the PRIMARY auth source in iframe mode.
//
// Token scoping:
//   - `resource` = SIMPLEAPP_LOGTO_AUDIENCE (the JWT `aud` claim target)
//   - `organizationId` = current org ID (when set) — adds `organization_id`
//     claim to the token for org-scoped authorization in Convex UDFs.

import { useEffect, useState, useCallback, useRef } from "react";
import { logtoClient, SIMPLEAPP_LOGTO_AUDIENCE } from "./logto-client";
import { requestParentAuthToken, useParentTokenAuth } from "./use-parent-token-auth";
import { isDemoModeRequested } from "./app-mode";

// Module-level org ID. Set externally (e.g. via postMessage from the sapps
// shell or a URL param) so this hook doesn't need props or context.
// `undefined` is valid — the token will be minted without an org claim.
let currentOrgId: string | undefined = undefined;

function isIframed(): boolean {
  return typeof window !== "undefined" && window.self !== window.top;
}

function isHandlingOidcCallback(): boolean {
  if (typeof window === "undefined" || !window.location) {
    return false;
  }
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.has("code") && searchParams.has("state");
}

function isInvalidGrantError(error: unknown): boolean {
  if (error instanceof Error && error.message.includes("invalid_grant")) {
    return true;
  }
  if (typeof error !== "object" || error === null) {
    return false;
  }
  if (
    "code" in error &&
    typeof error.code === "string" &&
    error.code.includes("invalid_grant")
  ) {
    return true;
  }
  return "error" in error && typeof error.error === "string" && error.error.includes("invalid_grant");
}

export function setCurrentOrgId(orgId: string | undefined): void {
  currentOrgId = orgId;
}

export function useConvexAuthFromLogto(): {
  isLoading: boolean;
  isAuthenticated: boolean;
  fetchAccessToken: ({
    forceRefreshToken,
  }: {
    forceRefreshToken: boolean;
  }) => Promise<string | null>;
} {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isIframedApp = isIframed();
  const parentTokenRef = useRef<string | null>(null);

  useParentTokenAuth(
    useCallback((token: string): void => {
      parentTokenRef.current = token;
      setIsAuthenticated(true);
      setIsLoading(false);
      console.log("[Frontend][Auth] Adopted parent-relayed token for iframed Convex auth");
    }, []),
    useCallback((): void => {
      if (parentTokenRef.current) {
        return;
      }
      setIsAuthenticated(false);
      setIsLoading(false);
      console.warn("[Frontend][Auth] Parent auth relay configuration unavailable; showing retry connection prompt");
    }, []),
    useCallback((): void => {
      parentTokenRef.current = null;
      setIsAuthenticated(false);
      setIsLoading(false);
      console.log("[Frontend][Auth] Parent session ended; cleared iframed Convex auth state");
    }, []),
  );

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      if (isIframedApp) {
        // In iframe mode the parent Sapps shell is the auth authority. Avoid the
        // local Logto refresh-token flow entirely: stale iframe refresh tokens
        // produce `invalid_grant`. useParentTokenAuth owns the cold-start
        console.debug("[Frontend][Auth] Waiting for parent auth relay before completing iframe startup");
        return;
      }
      if (isDemoModeRequested()) {
        console.debug("[Frontend][Auth] Demo mode requested; skipping auth startup");
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        const authed = await logtoClient.isAuthenticated();
        setIsAuthenticated(authed);
        console.debug("[Frontend][Auth] Standalone Logto auth check completed", { isAuthenticated: authed });
      } catch (error: unknown) {
        // If auth state can't be determined, treat as unauthenticated.
        if (error instanceof Error) {
          console.warn("[Frontend][Auth] Standalone Logto auth check failed", {
            errorName: error.name,
            message: error.message,
          });
        } else {
          console.warn("[Frontend][Auth] Standalone Logto auth check failed with non-Error value", {
            errorType: typeof error,
          });
        }
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    void checkAuth();
  }, [isIframedApp]);

  const fetchAccessToken = useCallback(
    async ({
      forceRefreshToken,
    }: {
      forceRefreshToken: boolean;
    }): Promise<string | null> => {
      if (isHandlingOidcCallback()) {
        console.debug("[Frontend][Auth] Deferring token fetch while OIDC callback is processing");
        return null;
      }

      if (isIframedApp && forceRefreshToken) {
        parentTokenRef.current = null;
      }
      const parentToken = parentTokenRef.current;
      if (parentToken && !forceRefreshToken) {
        return parentToken;
      }

      if (isIframedApp) {
        try {
          const freshParentToken = await requestParentAuthToken({ forceRefresh: forceRefreshToken });
          if (freshParentToken) {
            parentTokenRef.current = freshParentToken;
            setIsAuthenticated(true);
            setIsLoading(false);
            console.log("[Frontend][Auth] Retrieved fresh parent-relayed token for iframed Convex auth", {
              forceRefresh: forceRefreshToken,
            });
            return freshParentToken;
          }
          setIsAuthenticated(false);
          setIsLoading(false);
          console.warn("[Frontend][Auth] Parent auth relay returned no token for iframe Convex auth", {
            forceRefresh: forceRefreshToken,
          });
          return null;
        } catch (error: unknown) {
          parentTokenRef.current = null;
          setIsAuthenticated(false);
          setIsLoading(false);
          console.error("[Frontend][Auth] Parent auth relay request failed during iframe token fetch", {
            errorName: error instanceof Error ? error.name : "UnknownError",
            forceRefresh: forceRefreshToken,
          });
          return null;
        }
      }

      if (isDemoModeRequested()) {
        console.debug("[Frontend][Auth] Demo mode requested; skipping token fetch");
        return null;
      }

      try {
        // Convex signals `forceRefreshToken: true` when a previous token was
        // rejected. The Logto SDK has no `forceRefresh` param on
        // `getAccessToken`, so we clear the cached token first — the next
        // `getAccessToken` call will use the refresh token to mint a fresh one.
        if (forceRefreshToken) {
          await logtoClient.clearAccessToken();
        }

        // CRITICAL: Pass org ID as the second param for an org-scoped token.
        // `undefined` is OK — the token is still valid, just without an
        // `organization_id` claim.
        const token = await logtoClient.getAccessToken(
          SIMPLEAPP_LOGTO_AUDIENCE,
          currentOrgId,
        );
        return token;
      } catch (error: unknown) {
        if (isInvalidGrantError(error)) {
          try {
            await logtoClient.clearAllTokens();
            console.warn("[Frontend][Auth] Cleared stale standalone Logto tokens after invalid grant", {
              forceRefresh: forceRefreshToken,
            });
          } catch (clearError: unknown) {
            if (clearError instanceof Error) {
              console.error("[Frontend][Auth] Failed to clear stale standalone Logto tokens", {
                errorName: clearError.name,
                message: clearError.message,
              });
            } else {
              console.error("[Frontend][Auth] Failed to clear stale standalone Logto tokens with non-Error value", {
                errorType: typeof clearError,
              });
            }
          }
          setIsAuthenticated(false);
          setIsLoading(false);
        }
        // MUST catch and return null — Convex doesn't catch throws.
        // Returning null triggers one retry with forceRefreshToken: true,
        // then marks the user as unauthenticated.
        if (error instanceof Error) {
          console.error("[Frontend][Auth] Failed to fetch standalone access token", {
            errorName: error.name,
            message: error.message,
            forceRefresh: forceRefreshToken,
          });
        } else {
          console.error("[Frontend][Auth] Failed to fetch standalone access token with non-Error value", {
            errorType: typeof error,
            forceRefresh: forceRefreshToken,
          });
        }
        return null;
      }
    },
    [isIframedApp],
  );

  return { isLoading, isAuthenticated, fetchAccessToken };
}
